/**
 * The two decisions that keep a paying guest from being abandoned.
 *
 * WHAT WENT WRONG
 * ---------------
 * 1. A payment that lands around 0:00 was lost. The countdown's completion
 *    handler declared the session expired and the booth reset itself 15 s
 *    later, while the gateway went on to confirm the payment. The money is
 *    recorded against the operator, the guest walks away with nothing, and the
 *    refund is manual. `shouldKeepWatchingAfterExpiry` is the grace: the booth
 *    keeps asking the server for a few more seconds after the clock runs out.
 *
 * 2. Every connectivity blip minted a SECOND charge. The payment page created
 *    one on every offline→online flip, so a guest who had already opened the
 *    first checkout on their phone paid an order the booth was no longer
 *    watching. `shouldCreateNewCharge` resumes the live charge instead, and
 *    only mints a new one when there is nothing left to resume.
 *
 * Both live here, away from the page, because they are the rules worth testing.
 */

/**
 * How long the expired screen stays up before the booth recycles the session.
 * The grace below is sized to fit inside it, and the "I have paid" button puts
 * it back to the top so a late answer still has somewhere to land.
 */
export const AUTO_RESET_SECONDS = 15

/** How often the booth re-asks the server after the countdown hits 0:00. */
export const EXPIRY_GRACE_POLL_MS = 3000

/**
 * How many of those re-asks to make. Five at 3 s covers the 15 s the
 * auto-reset gives us, which is the window in which a payment confirmed just
 * after 0:00 can still be honoured on the booth.
 */
export const EXPIRY_GRACE_CHECKS = 5

export interface ChargeState {
  orderId?: string | null
  /** `pending` | `settlement` | `expire` | undefined */
  status?: string | null
  /**
   * A card-terminal order is open on the terminal beside the booth. Nothing
   * replaces it: the guest may have their card in the reader already, and a
   * second charge would split one customer across two orders. The booth
   * resumes polling instead, and the countdown-end rules decide from the
   * order's own status.
   */
  terminalOrderOpen?: boolean
}

export interface PausedCountdown {
  /** Seconds left on the payment countdown when the network dropped. */
  pausedSeconds: number
  /** When it was paused (epoch ms), or null if it never was. */
  pausedAtMs: number | null
}

/**
 * Seconds still left on a countdown that was paused while offline.
 *
 * The booth pauses its countdown when the network drops, so the time that
 * passed offline still has to come off the clock: the gateway kept counting.
 */
export function remainingSecondsAfterPause(
  paused: PausedCountdown,
  nowMs: number
): number {
  if (paused.pausedAtMs === null || !Number.isFinite(paused.pausedAtMs)) return 0
  if (!Number.isFinite(paused.pausedSeconds) || paused.pausedSeconds <= 0) return 0
  const elapsedSeconds = Math.floor((nowMs - paused.pausedAtMs) / 1000)
  return Math.max(0, paused.pausedSeconds - elapsedSeconds)
}

/**
 * On reconnect: mint a new charge, or resume the one we already have?
 *
 * Resuming is the default whenever a charge could still be paid, because the
 * guest may already be looking at its checkout on their phone. A new charge is
 * only right when there is none, or when the old one's window has run out.
 */
export function shouldCreateNewCharge(
  input: ChargeState & PausedCountdown & { nowMs: number }
): boolean {
  // Already paid: the page navigates onward, it must never charge again.
  if (input.status === 'settlement') return false
  // Nothing to resume.
  if (!input.orderId) return true
  // A terminal is holding this one. Even a window that ran out is the
  // terminal's to finish or reject; Studio cancels it when it can.
  if (input.terminalOrderOpen) return false
  // An expired charge is dead; a fresh one is the only way forward.
  if (input.status === 'expire') return true
  return (
    remainingSecondsAfterPause(
      { pausedSeconds: input.pausedSeconds, pausedAtMs: input.pausedAtMs },
      input.nowMs
    ) <= 0
  )
}

/**
 * After the countdown has run out: is it still worth asking the server?
 *
 * Stops as soon as the payment settles (the page navigates), when the booth is
 * offline (nothing to ask), or once the grace checks are used up.
 */
export function shouldKeepWatchingAfterExpiry(input: {
  isExpired: boolean
  isOnline: boolean
  status?: string | null
  checksDone: number
  maxChecks?: number
}): boolean {
  if (!input.isExpired || !input.isOnline) return false
  if (input.status === 'settlement') return false
  return input.checksDone < (input.maxChecks ?? EXPIRY_GRACE_CHECKS)
}

/**
 * Does a status answer still belong to the order the page is showing?
 *
 * A status request can outlive its order: coming back online after the window
 * ran out, the page mints a new charge and asks about the old one in the same
 * breath. The old order's "expired" must not land on the new charge.
 */
export function isAnswerForCurrentOrder(
  askedOrderId: string | null | undefined,
  currentOrderId: string | null | undefined
): boolean {
  return Boolean(askedOrderId) && askedOrderId === currentOrderId
}

/**
 * How many times one expired screen may be held open with "I have paid, check
 * again". Each press restarts the auto-reset, so without a cap one tap every
 * few seconds would keep a public booth from ever recycling.
 */
export const MAX_MANUAL_RECHECKS = 3

export function canCheckAgainManually(pressesSoFar: number): boolean {
  return pressesSoFar < MAX_MANUAL_RECHECKS
}
